// feat/inquiry-chat (2026-05-26) — 외부 바이어 '내 문의' 페이지 (목록 + 1:1 통역 채팅 스레드)
// 백엔드: GET /buyer-app/inventory/my-inquiries, GET/POST /buyer-app/inventory/inquiries/:id/messages.
// 업체 답장은 바이어 locale 로 통역되어 노출 (bodyTranslated). 본인 메시지는 원문.

import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import { useNavigate } from 'react-router-dom';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import EmptyState from './EmptyState';
import { showToast } from './Toast';
import { useT, useLocale } from '../../i18n/I18nProvider';

function fmtDate(s) { if (!s) return ''; return String(s).slice(0, 16).replace('T', ' '); }

// ─── 스레드 (대화) 뷰 ───
function ThreadView(props) {
  var t = props.t;
  var locale = props.locale;   // 바이어 현재 UI 언어 (useLocale) — 통역 src/tgt 신호
  var inquiry = props.inquiry;
  var navigate = props.navigate;
  var [items, setItems] = useState([]);
  var [header, setHeader] = useState(null);
  var [loading, setLoading] = useState(true);
  var [text, setText] = useState('');
  var [sending, setSending] = useState(false);

  var load = useCallback(function() {
    setLoading(true);
    authBuyerFetch(API + '/buyer-app/inventory/inquiries/' + inquiry.id + '/messages?locale=' + encodeURIComponent(locale || 'en'), {})
      .then(function(r){ if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); } return r.json(); })
      .then(function(d){ if (d && d.success && d.data) { setItems(d.data.items || []); setHeader(d.data.header || null); } setLoading(false); })
      .catch(function(e){ if (e && e.message === 'unauthorized') return; setLoading(false); });
  }, [inquiry.id, navigate, locale]);

  useEffect(function(){ load(); }, [load]);

  function send() {
    var body = text.trim();
    if (!body || sending) return;
    setSending(true);
    authBuyerFetch(API + '/buyer-app/inventory/inquiries/' + inquiry.id + '/messages', { method: 'POST', body: JSON.stringify({ body: body, locale: locale || 'en' }) })
      .then(function(r){ if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); } return r.json(); })
      .then(function(d){ setSending(false); if (d && d.success) { setText(''); load(); } else showToast(t('commonError'), { type: 'error' }); })
      .catch(function(e){ setSending(false); if (e && e.message === 'unauthorized') return; showToast(t('commonNetwork'), { type: 'error' }); });
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px 40px' }}>
      <button type="button" onClick={props.onBack}
        style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 0', marginBottom: 12 }}>
        {t('myInqBack')}
      </button>
      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '68vh' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{(header && header.carType) || inquiry.carType || '-'}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>🏢 {(header && header.companyName) || inquiry.companyName || ''}</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>{t('commonLoading')}</div>
          ) : items.map(function(m, i) {
            var mine = m.isMine;   // 바이어 본인
            return (
              <div key={m.id + '-' + i} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, textAlign: mine ? 'right' : 'left' }}>{m.senderName || ''}</div>
                <div style={{ background: mine ? '#1B3A5C' : '#fff', color: mine ? '#fff' : '#0f172a', border: mine ? 'none' : '1px solid #e2e8f0', borderRadius: 12, padding: '8px 12px', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {/* 업체 답장(ko)은 바이어 locale 번역 우선 + 원문 보조 */}
                  {(!mine && m.bodyTranslated)
                    ? (<><div>{m.bodyTranslated}</div><div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{m.body}</div></>)
                    : m.body}
                </div>
                <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 2, textAlign: mine ? 'right' : 'left' }}>{fmtDate(m.createdAt)}</div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: '12px 14px', borderTop: '1px solid #eee', display: 'flex', gap: 8 }}>
          <input value={text} onChange={function(e){ setText(e.target.value); }} onKeyDown={function(e){ if (e.key === 'Enter') send(); }}
            placeholder={t('myInqReplyPlaceholder')}
            style={{ flex: 1, border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none' }} />
          <button type="button" onClick={send} disabled={sending}
            style={{ padding: '0 18px', borderRadius: 10, border: 'none', background: sending ? '#cbd5e1' : 'linear-gradient(135deg,#1B3A5C 0%,#3b82f6 100%)', color: '#fff', fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer' }}>
            {sending ? '…' : t('myInqSend')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuctionBuyerMyInquiries() {
  var navigate = useNavigate();
  var t = useT();
  var loc = useLocale();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selected, setSelected] = useState(null);

  var load = useCallback(function() {
    setLoading(true);
    authBuyerFetch(API + '/buyer-app/inventory/my-inquiries', {})
      .then(function(r){ if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); } return r.json(); })
      .then(function(d){ if (d && d.success && d.data) setItems(d.data.items || []); setLoading(false); })
      .catch(function(e){ if (e && e.message === 'unauthorized') return; setLoading(false); });
  }, [navigate]);

  useEffect(function(){ load(); }, [load]);

  if (selected) {
    return (
      <AuctionBuyerLayout activeKey="my-inquiries">
        <ThreadView t={t} locale={loc.locale} inquiry={selected} navigate={navigate} onBack={function(){ setSelected(null); load(); }} />
      </AuctionBuyerLayout>
    );
  }

  return (
    <AuctionBuyerLayout activeKey="my-inquiries">
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px 80px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>{t('myInqTitle')}</h2>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>{t('commonLoading')}</div>
        ) : items.length === 0 ? (
          <EmptyState icon="💬" title={t('myInqEmpty')} hint={t('myInqEmptyHint')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(function(it) {
              return (
                <div key={it.id} onClick={function(){ setSelected(it); }}
                  style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{it.carType || '-'}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>🏢 {it.companyName || ''}</div>
                    <div style={{ fontSize: 13, color: '#475569', marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.lastMessage || ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: '#cbd5e1' }}>{fmtDate(it.lastAt)}</div>
                    {it.unread > 0 && <span style={{ display: 'inline-block', marginTop: 6, background: '#ef4444', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{it.unread}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerMyInquiries;
